/**
 * Types for the Production Insights feature (Daily + Recap)
 * Supports both in-progress daily insights and wrapped production recap
 */

export type ProductionStatus = 'in_progress' | 'wrapped';
export type ConfidenceLevel = 'low' | 'medium' | 'high';
export type CorrelationStrength = 'strong' | 'moderate' | 'weak' | 'critical';

export interface ProductionStatusData {
  production_status: ProductionStatus;
  current_day: number;
  total_scheduled_days: number;
  days_with_data: number;
}

export interface SummaryMetric {
  value?: number;
  total?: number;
  shot?: number;
  planned?: number;
  scheduled?: number;
  dropped?: number;
  variance?: number;
  label: string;
}

export interface ProductionSummary {
  days: SummaryMetric;
  pages: SummaryMetric;
  scenes: SummaryMetric;
  budget: SummaryMetric;
}

export interface ProductionInsight {
  id: string;
  title: string;
  finding: string;
  description: string;
  correlation_strength: CorrelationStrength;
  confidence_level: ConfidenceLevel;
  sample_size: number;
  data: Record<string, any>;
  copilot_prompt: string;
  precomputed_explanation?: string;
}

export interface WorkloadOvertimeData {
  summary: Array<{
    workload_category: string;
    total_days: number;
    overtime_days: number;
    overtime_rate: number;
    avg_pages_planned: number;
    avg_pages_actual: number;
  }>;
  total_days: number;
  total_overtime_days: number;
  heavy_overtime_rate: number;
  light_overtime_rate: number;
}

export interface DroppedScenesData {
  total_scheduled: number;
  total_shot: number;
  total_dropped: number;
  completion_percentage: number;
  term: 'dropped' | 'owed';
}

export interface FinancialData {
  final_variance: number;
  budget_status: 'under_budget' | 'over_budget' | 'on_budget';
  total_overtime_days: number;
  total_days: number;
  overtime_rate: number;
  avg_variance_ot_days: number;
  avg_variance_non_ot_days: number;
  department_variances: Array<{
    department_name: string;
    total_variance: number;
  }>;
}

export interface EpisodeEfficiencyData {
  episodes: Array<{
    episode_number: string;
    total_pages_shot: number;
    days_worked: number;
    pages_per_day: number;
    efficiency_rank: number;
    first_day_worked: number;
    last_day_worked: number;
    span_days: number;
  }>;
  most_efficient: { episode: string; pages_per_day: number };
  least_efficient: { episode: string; pages_per_day: number };
  average_pages_per_day: number;
}

export interface ScheduleAccuracyData {
  by_day_of_week: Array<{
    day_name: string;
    day_of_week: number;
    total_days: number;
    avg_accuracy: number;
    days_met_target: number;
    target_met_rate: number;
  }>;
  by_category: Array<{
    accuracy_category: string;
    count: number;
    avg_accuracy: number;
  }>;
  best_day: { name: string; accuracy: number };
  worst_day: { name: string; accuracy: number };
  overall_target_met_rate: number;
}

export interface ProductionInsightsData {
  status: ProductionStatusData;
  summary: ProductionSummary;
  insights: {
    workloadOvertime: ProductionInsight;
    droppedScenes: ProductionInsight;
    financial: ProductionInsight;
    episodeEfficiency: ProductionInsight;
    scheduleAccuracy: ProductionInsight;
  };
}

export interface ProductionInsightsApiResponse {
  success: boolean;
  data: ProductionInsightsData | null;
  error?: string;
  timestamp: string;
}

export interface SingleInsightApiResponse {
  success: boolean;
  data: ProductionInsight | null;
  error?: string;
  timestamp: string;
}

export interface StatusApiResponse {
  success: boolean;
  data: ProductionStatusData | null;
  error?: string;
  timestamp: string;
}

export interface SummaryApiResponse {
  success: boolean;
  data: {
    production_status: ProductionStatus;
    current_day: number;
    total_days: number;
    summary: ProductionSummary;
  } | null;
  error?: string;
  timestamp: string;
}

export const INSIGHT_ICONS: Record<string, string> = {
  workloadOvertime: 'Schedule',
  droppedScenes: 'MovieFilter',
  financial: 'AttachMoney',
  episodeEfficiency: 'Speed',
  scheduleAccuracy: 'CheckCircle',
};

export const INSIGHT_COLORS: Record<string, string> = {
  workloadOvertime: '#9F7AEA',
  droppedScenes: '#ED8936',
  financial: '#48BB78',
  episodeEfficiency: '#4299E1',
  scheduleAccuracy: '#38B2AC',
};

export const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  low: '#A0AEC0',
  medium: '#ED8936',
  high: '#48BB78',
};

export const CORRELATION_STRENGTH_COLORS: Record<CorrelationStrength, string> = {
  strong: '#48BB78',
  moderate: '#ED8936',
  weak: '#A0AEC0',
  critical: '#F56565',
};
